import {
  CanonicalDataType,
  type SchemaFieldRecord,
} from "@/shared/domain/topics";

import {
  type SessionTopicRecord,
  type SessionTopicStatsRecord,
} from "./SessionTopicRecord";

/**
 * Topics whose data is never read for stats, so every field gets none: images
 * and point clouds, in every spelling a recording can carry: ROS 1
 * (`pkg/Type`), ROS 2 (`pkg/msg/Type`) and Foxglove protobuf
 * (`foxglove.Type`). Mirrors `COUNT_ONLY_SCHEMA_NAMES` in
 * `packages/roboto_internal/src/roboto_internal/domain/topic_stats/field_rule.py`.
 */
const COUNT_ONLY_SCHEMA_NAMES: ReadonlySet<string> = new Set([
  "sensor_msgs/Image",
  "sensor_msgs/msg/Image",
  "sensor_msgs/CompressedImage",
  "sensor_msgs/msg/CompressedImage",
  "sensor_msgs/PointCloud2",
  "sensor_msgs/msg/PointCloud2",
  "foxglove.RawImage",
  "foxglove.CompressedImage",
  "foxglove.CompressedVideo",
  "foxglove.PointCloud",
  "foxglove_msgs/RawImage",
  "foxglove_msgs/msg/RawImage",
  "foxglove_msgs/CompressedImage",
  "foxglove_msgs/msg/CompressedImage",
  "foxglove_msgs/CompressedVideo",
  "foxglove_msgs/msg/CompressedVideo",
  "foxglove_msgs/PointCloud",
  "foxglove_msgs/msg/PointCloud",
]);

const NUMERIC_TYPES: ReadonlySet<CanonicalDataType> = new Set([
  CanonicalDataType.Number,
  CanonicalDataType.LatDegFloat,
  CanonicalDataType.LonDegFloat,
  CanonicalDataType.LatDegInt,
  CanonicalDataType.LonDegInt,
]);

const ARRAY_TYPES: ReadonlySet<CanonicalDataType> = new Set([
  CanonicalDataType.Array,
  CanonicalDataType.NumberArray,
]);

/** Why a field gets no stats. Mirrors `SkipReason` in `field_rule.py`. */
export type FieldSkipReason =
  "array" | "inside_array" | "unsupported_type" | "count_only_topic";

/** Which family of stats an eligible field gets. */
export type FieldStatsKind = "numeric" | "boolean";

/** What the stats rule decides for one field of a schema. */
export type FieldStatsPlan =
  | { kind: FieldStatsKind; skipReason: null }
  | { kind: null; skipReason: FieldSkipReason };

const pathKey = (path: readonly string[]) => JSON.stringify(path);

/**
 * Decide, from the schema alone, which stats each field gets, in the order of
 * `fields`. Numeric and boolean scalar leaves get stats; arrays, fields inside
 * arrays and every other type get none, and neither does any field of an image
 * or point-cloud schema. Mirrors `plan_field_stats` in `field_rule.py`, which
 * decides what the session_metrics action computes.
 */
function planFieldStats(
  schemaName: string | null,
  fields: readonly SchemaFieldRecord[],
): FieldStatsPlan[] {
  const arrayPaths = new Set(
    fields
      .filter(
        (field) =>
          ARRAY_TYPES.has(field.canonical_data_type) ||
          field.data_type.endsWith("]"),
      )
      .map((field) => pathKey(field.path_in_schema)),
  );
  const countOnly =
    schemaName !== null && COUNT_ONLY_SCHEMA_NAMES.has(schemaName);
  return fields.map((field): FieldStatsPlan => {
    const path = field.path_in_schema;
    if (countOnly) {
      return { kind: null, skipReason: "count_only_topic" };
    }
    if (arrayPaths.has(pathKey(path))) {
      return { kind: null, skipReason: "array" };
    }
    for (let depth = 1; depth < path.length; depth++) {
      if (arrayPaths.has(pathKey(path.slice(0, depth)))) {
        return { kind: null, skipReason: "inside_array" };
      }
    }
    if (NUMERIC_TYPES.has(field.canonical_data_type)) {
      return { kind: "numeric", skipReason: null };
    }
    if (field.canonical_data_type === CanonicalDataType.Boolean) {
      return { kind: "boolean", skipReason: null };
    }
    return { kind: null, skipReason: "unsupported_type" };
  });
}

/** One row of a topic's fields table: a schema field, the rule's decision, and its stats. */
export interface TopicFieldRow {
  field: SchemaFieldRecord;
  plan: FieldStatsPlan;
  /** The field's stats over the session, or `null` when none were published for its path. */
  stats: Partial<Record<string, number>> | null;
}

/** From the topic's first to its last message in the session, or `null` when its data carries no timestamps. */
export function sessionTopicDurationNs({
  start_time_ns: start,
  end_time_ns: end,
}: SessionTopicRecord): bigint | null {
  return start === null || end === null ? null : end - start;
}

/**
 * Messages per second over the topic's span in the session, in Hz: N messages
 * from the first to the last are N − 1 intervals. `null` when no rate
 * follows: the message count is not yet known, it is below two, or the span
 * is empty.
 */
export function sessionTopicFrequencyHz(
  topic: SessionTopicRecord,
): number | null {
  const count = topic.message_count;
  const duration = sessionTopicDurationNs(topic);
  if (count === null || count < 2 || duration === null || duration <= 0n) {
    return null;
  }
  return (count - 1) / (Number(duration) / 1e9);
}

/**
 * The fields table for one of a topic's schemas: each of `fields`, in order,
 * with the stats rule's decision and the stats published for its path. Stats
 * are per path across every schema of the topic, so two schemas that share a
 * path show the same numbers for it.
 */
export function sessionTopicFieldRows(
  schemaName: string | null,
  fields: readonly SchemaFieldRecord[],
  stats: Pick<SessionTopicStatsRecord, "fields">,
): TopicFieldRow[] {
  const statsByPath = new Map(
    stats.fields.map((entry) => [pathKey(entry.path), entry.stats]),
  );
  const plans = planFieldStats(schemaName, fields);
  return fields.map((field, index) => ({
    field,
    plan: plans[index],
    stats: statsByPath.get(pathKey(field.path_in_schema)) ?? null,
  }));
}
